import type { ClientCommunityPlan } from "api-types/locationPlans/communityPlansResponses";
import type { ClientMasterPlan } from "api-types/masterPlans/masterPlansResponses";
import type { Building } from "building-types/building/buildingTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { DocOption } from "building-types/options/optionSet";
import type { OptionSetEntityConnectedTable } from "building-types/options/optionSetEntityRelationsTypes";
import type { Table } from "common/data/table";
import { formatLargeDollars } from "common/utils/formatDollars";
import { isBooleanOptionSet, optionSetDefaultOptionId } from "doc/options/optionSetBaseUtils";
import { exteriorPaletteOptionSetIdFromBuilding, isExteriorPaletteOptionSet } from "doc/options/paletteOptionSet";

export function getOptionsForPricing(
  areConnected: OptionSetEntityConnectedTable,
  building: Building | null,
  optionChoices?: OptionChoices,
): Table<DocOption> {
  const result = {};
  const buildingPaletteOptionSetId = building ? exteriorPaletteOptionSetIdFromBuilding(building) : undefined; // which palette set is the building using?
  const optionSets = building?.evaluatedOptionSets ?? building?.doc.optionSets ?? {};
  const choices = optionChoices ?? building?.optionChoices ?? [];

  for (const optionSetId in choices) {
    const optionSet = optionSets[optionSetId];

    if (optionSet && !optionSet.disabled && optionSet.options && areConnected[optionSetId]) {
      const currentChoice = choices[optionSetId];
      const defaultChoice = optionSetDefaultOptionId(optionSet, optionSets);
      const chosenOption = optionSet.options[currentChoice];

      if (chosenOption && !chosenOption.disabled && currentChoice !== defaultChoice) {
        if (isBooleanOptionSet(optionSet) && currentChoice === "true") {
          result[optionSetId] = chosenOption;
        } else if (!isExteriorPaletteOptionSet(optionSet) || optionSetId === buildingPaletteOptionSetId) {
          // either an option set or palette set in use by the building
          result[optionSetId] = chosenOption;
        }
      }
    }
  }
  return result;
}

export function formatOptionPrice(price: number): string {
  return price >= 100000 ? `${Math.round(price / 1000)}K` : price.toLocaleString();
}

// TODO: This should live in pricing file
export function getPlanStartingPriceString(plan: ClientMasterPlan | ClientCommunityPlan): string {
  if (!plan) {
    return "";
  }

  return `From ${formatLargeDollars(plan.basePrice ?? NaN)}`;
}
